<template>
  <v-dialog
    id="clusterNameModal"
    :model-value="value"
    fullscreen
    content-class="cdt-tree-modal"
    attach=".cdt-page"
    @click:outside="closeModal"
    @keydown.esc="closeModal"
  >
    <BaseDialogCard :title="customerDecisionTreeTitle" @close="closeModal">
      <div class="cdt-tree-container">
        <div class="container-block flex-grow-1">
          <div class="tree ma-0 pb-1">
            <ProgressBar v-if="loading" class="pt-5" />

            <div v-else class="treeview-container">
              <div v-if="cdt" class="treeview-column-labels">
                <div
                  v-for="(val, index) in selectedAttributes"
                  :key="val.attributeId"
                  class="d-flex treeview-column-label justify-space-between"
                >
                  <v-row class="attribute-name ma-0 align-center" no-gutters>
                    <v-col cols="8" class="pa-0 pr-1">
                      <p>
                        {{ getAttributeName(val.attributeId) }}
                      </p>
                    </v-col>

                    <v-col cols="2" class="text-center pa-0">
                      <span>
                        <v-tooltip location="top">
                          <template #activator="{ props }">
                            <span v-bind="props">
                              {{ coefficientByLevel[index] }}
                            </span>
                          </template>
                          {{ tkey('cdtList.coefficient') }}: {{ coefficientByLevel[index] }}
                        </v-tooltip>
                      </span>
                    </v-col>

                    <v-col cols="2" class="text-center pa-0">
                      <span v-if="val.rank" class="rank-circle">
                        <v-tooltip location="top">
                          <template #activator="{ props }">
                            <span v-bind="props">
                              {{ val.rank }}
                            </span>
                          </template>
                          {{ tkey('cdtList.rank') }}: {{ val.rank }}
                        </v-tooltip>
                      </span>
                    </v-col>
                  </v-row>

                  <div
                    v-if="getToggleButton(index)"
                    class="treeview-column-controls d-flex flex-column justify-center pb-2"
                  >
                    <v-btn class="treeview-column-button" @click="toggleNodes(index)">
                      <v-icon>
                        {{ toggleIcon(index) }}
                      </v-icon>
                    </v-btn>
                  </div>
                </div>
              </div>

              <v-treeview
                v-if="treeData"
                v-model:opened="openedNodes"
                density="compact"
                class="rtls-treeview"
                item-value="_id"
                :items="treeData"
                open-on-click
                indent-lines
                separate-roots
              >
                <template #prepend="{ item: node }">
                  <div class="attributes-box d-flex flex-column">
                    <div class="attribute-value d-flex align-center mb-1">
                      <p>
                        <v-tooltip location="top">
                          <template #activator="{ props }">
                            <span v-bind="props">
                              {{ node.attributeValue }}
                            </span>
                          </template>
                          {{ node.attributeValue }}
                        </v-tooltip>
                      </p>
                    </div>
                  </div>
                </template>
              </v-treeview>
            </div>
          </div>
        </div>
      </div>
    </BaseDialogCard>
  </v-dialog>
</template>

<script>
import { filter, get, intersection, isEmpty, isUndefined, reduce, size, uniq } from 'lodash';
import { mapActions, mapState } from 'pinia';

// enums
import scenarioCdtTypes from '@enums/scenario-cdt-types';

// composables
import { useInternationalization } from '@/composables/use-internationalization';
import { useFormatNumber } from '@/composables/use-format-number';

// stores
import { useContextStore } from '@/stores/context';
import { useScenarioCdtsStore } from '@/stores/scenario-cdts';
import { useScenariosStore } from '@/stores/scenarios';

export default {
  props: {
    value: {
      type: Boolean,
      required: true,
    },

    selectedCannGroup: {
      type: Object,
      required: true,
    },

    cdtRef: {
      required: true,
      type: Object,
    },
  },

  emits: ['close'],

  setup() {
    const { tkey } = useInternationalization('customerDecisionTree');

    const { formatNumber } = useFormatNumber();

    return { tkey, formatNumber };
  },

  data() {
    return {
      cdt: null,
      treeData: null,
      openedNodes: [],
      parentNodesByLevel: {},
    };
  },

  computed: {
    ...mapState(useScenariosStore, ['selectedScenario', 'attributesById']),
    ...mapState(useContextStore, ['clientConfig']),
    ...mapState(useScenarioCdtsStore, ['scenarioCdts', 'loading']),

    selectedAttributes() {
      if (this.cdt.type === scenarioCdtTypes.userModified) {
        return filter(this.cdt.attributes, at => at.isSelected);
      }
      return filter(this.cdt.attributes, at => at.rank).sort((a, b) => a.rank - b.rank);
    },

    customerDecisionTreeTitle() {
      const cannGroupKey = get(this.selectedCannGroup, 'key');
      return this.tkey('treeView.title', {
        cannGroupName: get(this.selectedCannGroup, 'name', cannGroupKey),
      });
    },

    coefficientByLevel() {
      const cutOff = this.clientConfig.cdt.coefficientDisplayCutOff;
      return reduce(
        this.selectedAttributes,
        (acc, sa, level) => {
          if (sa.coefficient < cutOff) {
            const cutOffFormatted = this.formatNumber({ number: cutOff, format: 'float' });
            acc[level] = `< ${cutOffFormatted}`;
          } else {
            acc[level] = this.formatNumber({ number: sa.coefficient, format: 'float' });
          }
          return acc;
        },
        {}
      );
    },
  },

  watch: {
    cdtRef: {
      deep: true,
      handler() {
        this.init(this.selectedScenario.cannGroups);
      },
    },
  },

  async created() {
    this.init(this.selectedScenario.cannGroups);
  },

  methods: {
    ...mapActions(useScenarioCdtsStore, ['fetchScenarioCdts']),

    getAttributeName(attributeId) {
      return this.attributesById[attributeId].name;
    },

    async init() {
      this.openedNodes = [];
      await this.fetchScenarioCdts();
      this.cdt = this.scenarioCdts.find(cdt => cdt._id === this.cdtRef._id);
      this.treeData = this.unflatten(get(this.cdt, 'cdt', []));
      this.openedNodes = uniq(this.openedNodes);
    },

    getOpenNodes(level) {
      const levelParentNodes = [...(this.parentNodesByLevel[level] || [])];
      return size(intersection(this.openedNodes, levelParentNodes));
    },

    toggleNodes(level) {
      const nodesToOpen = [];
      const levelOpenNodes = this.getOpenNodes(level);

      // Expand parent nodes
      for (let i = 0; i < level; i += 1) {
        nodesToOpen.push(...this.parentNodesByLevel[i]);
      }
      // If at least one node is closed in the current level, expand all nodes
      if (levelOpenNodes < this.parentNodesByLevel[level].size) {
        nodesToOpen.push(...this.parentNodesByLevel[level]);
      }
      this.openedNodes = nodesToOpen;
    },

    toggleIcon(level) {
      return this.getOpenNodes(level) === this.parentNodesByLevel[level].size
        ? 'mdi-menu-down'
        : 'mdi-menu-right';
    },

    getToggleButton(level) {
      // hide toggle button for levels that do not contain nodes or contain nodes without children
      return !isUndefined(this.parentNodesByLevel[level]);
    },

    unflatten(array, parent, tree, level = 0) {
      tree = typeof tree !== 'undefined' ? tree : [];
      parent = typeof parent !== 'undefined' ? parent : { _id: null };
      parent.level = level;

      if (parent._id && level < this.clientConfig.cdt.defaultExpandDepth) {
        this.openedNodes.push(parent._id);
      }
      const children = array.filter(function (child) {
        return child.parentId === parent._id;
      });

      if (!isEmpty(children)) {
        if (parent._id == null) {
          tree = children;
        } else {
          if (!this.parentNodesByLevel[level]) {
            // Use Set to ensure ids are unique
            this.parentNodesByLevel[level] = new Set();
          }
          this.parentNodesByLevel[level].add(parent._id);
          children.forEach(c => {
            if (c._id && level < this.clientConfig.cdt.defaultExpandDepth) {
              this.openedNodes.push(c._id);
            }
          });
          level += 1;
          parent.children = children;
        }
        children.forEach(child => {
          child.level = level;
          this.unflatten(array, child, undefined, level);
        });
      }
      return tree;
    },

    closeModal() {
      this.parentNodesByLevel = {};
      this.$emit('close');
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@style/base/_variables.scss';

.cdt-tree-container {
  font-size: 12px;
  max-width: none !important;
  color: $assortment-text-colour;
  height: 100%;

  .header {
    border-bottom: 1px solid $assortment-divider-colour;
  }

  .container-block {
    overflow: auto;
    position: relative;
    height: 100%;

    .tree {
      display: flex;
      overflow-y: auto;
      height: 100%;
      width: fit-content;

      .treeview-container {
        padding: 0 $assortment-sidepanel-toggle-height 0 0;
      }

      .attribute-name {
        width: 186px;

        p {
          font-weight: bold;
          font-size: 1.2rem;
          margin-bottom: 2px;
        }
      }

      .attribute-value {
        background: $assortment-scope-background-colour;
        width: 220px;
        height: 22px;
        border-radius: 3px;
        padding: 0 20px 0 10px;
        margin-right: 10px;

        p {
          font-size: 1.2rem;
          margin-bottom: 0px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        &--all-products {
          margin-top: 20px;
          margin-right: 0;
        }

        &.hidden {
          display: none !important;
        }
      }
    }
  }

  .page-actions-container {
    border-top: 1px solid $assortment-panel-border-divider-colour;
  }
}

.rank-circle {
  background: $rank-background;
  padding: 1px 6px 2px;
  border-radius: 50%;
  color: white;
}

.treeview-column {
  /* Top column labels */
  &-labels {
    width: 100%;
    display: flex;
    margin-bottom: 5px;
    position: relative;
    background-color: white;
    padding: 10px 10px 10px 0px;
  }

  &-label {
    background: $assortment-attribute-colour;
    border: 1px solid $assortment-scenarios-border-colour;
    border-radius: 3px;
    margin-right: 12px;
    padding: 4px 5px 4px 10px;
    width: 220px;

    p {
      line-height: 1.8rem;
    }
  }

  &-button {
    background-color: transparent !important;
    box-shadow: none;
    color: $assortment-tree-toggle-colour;
    height: 18px !important;
    min-width: unset !important;
    padding: 0 !important;
    width: 18px !important;
  }
}
</style>
